import { Component, Input, signal, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { LabelsProviderPipe } from 'src/app/pipes/labels-provider/labels-provider.pipe';
import { FadeInOut } from 'src/app/animations/animations';
import { DropdownModule } from 'primeng/dropdown';
import {
  InstallationDownloadHttpService,
  OperatingSystem,
} from 'src/app/services/httpServices/installationDownloadHttpService/installation-download-http.service';
import { LoggerService } from 'src/app/services/infra/logger/logger.service';

@Component({
  selector: 'app-devices-enrollment-key',
  standalone: true,
  imports: [CommonModule, FormsModule, LabelsProviderPipe, DropdownModule],
  templateUrl: './devices-enrollment-key.component.html',
  styleUrl: './devices-enrollment-key.component.scss',
  animations: [FadeInOut],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DevicesEnrollmentKeyComponent {
  @Input() public enrollmentKey: string | undefined;

  private readonly _installationDownloadService = inject(InstallationDownloadHttpService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _logger = inject(LoggerService);

  public readonly isKeyVisible = signal(false);
  public readonly showCopyNotification = signal(false);

  public readonly platformOptions = [
    { label: 'Windows x64', value: OperatingSystem.windowsX64 },
    { label: 'Windows ARM', value: OperatingSystem.windowsARM },
    { label: 'MacOS', value: OperatingSystem.macos },
  ];

  public selectedPlatform: OperatingSystem | null = null;

  public get maskedKey(): string {
    if (!this.enrollmentKey) {
      return '';
    }
    return '•'.repeat(64);
  }

  public get displayKey(): string {
    if (!this.enrollmentKey) {
      return '';
    }
    return this.isKeyVisible() ? this.enrollmentKey : this.maskedKey;
  }

  public toggleKeyVisibility(): void {
    this.isKeyVisible.update((visible) => !visible);
  }

  public copyToClipboard(): void {
    if (this.enrollmentKey) {
      navigator.clipboard.writeText(this.enrollmentKey);
      this.showCopyNotification.set(true);
    }
  }

  public onPlatformSelect(): void {
    if (this.selectedPlatform) {
      this.downloadInstaller(this.selectedPlatform);
    }
  }

  private downloadInstaller(os: OperatingSystem): void {
    this._installationDownloadService
      .getDownloadUrl(os)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (response) => {
          window.open(response.fileUrl, '_blank');
          // Reset selection after download starts
          this.selectedPlatform = null;
        },
        error: (error) => {
          this._logger.error('Failed to download installer:', error);
        },
      });
  }
}
